import React, { useCallback } from 'react';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { IManualCheckDisbursementData } from 'types/order/escrow/IManualCheckDisbursementData';
import { ICheckDisbursementFormData } from '../../../../../../types/order/escrow/checks/ICheckDisbursementFormData';
import { VoidConfirmationModal } from './VoidConfirmationModal';
import { VoidReason } from './VoidReason';
import { Flex } from '@zillow/constellation';
import { VoidCheckDetails } from './VoidCheckDetails';
import { VoidCheckInHand } from './VoidCheckInHand';
import { voidCheckSchema } from './schemas';
import { IVoidData } from './IVoidData';
import { checkVoidReasonOptions, eCheckVoidReason } from 'types/accounting/eCheckVoidReason';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { useVoidCheckDisbursementMutation } from 'API/order/escrow/CheckDisbursementAPI';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useHasPermissions } from 'components/accessControl/hooks/useHasPermissions';

export interface ISendWireButtonProps {
  checkDisbursement: IManualCheckDisbursementData | ICheckDisbursementFormData;
}

export const VoidCheckButton: React.FC<ISendWireButtonProps> = ({ checkDisbursement }) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);

  const [onVoidCheck] = useVoidCheckDisbursementMutation();

  const onVoid = useCallback(
    async (values: IVoidData) => {
      const result = await onVoidCheck({
        orderId,
        check: {
          id: checkDisbursement.id,
          void_reason: values.voidReason as eCheckVoidReason,
          check_in_hand: values.checkInHand,
          void_reason_description:
            values.voidReason === eCheckVoidReason.Other ? values.voidReasonDescription : undefined,
        },
      });
      checkForMutationSuccess(result, 'voiding check');
    },
    [orderId, checkDisbursement.id, onVoidCheck]
  );

  // Sent but not disbursed or voided
  const isVoidable =
    !checkDisbursement.voidedAt &&
    (checkDisbursement.sentAt || checkDisbursement.sentAtDate || checkDisbursement.printedDate) &&
    !checkDisbursement.completedAt &&
    !checkDisbursement.completedAtDate;
  const hasPermission = useHasPermissions([ePermissions.EscrowFilesEdit]);
  if (!hasPermission || !isVoidable) {
    return null;
  }

  return (
    <VoidConfirmationModal
      onVoid={onVoid}
      button-data-test-id={`void-check-button-${checkDisbursement.id}`}
      whatAreWeVoiding="Check"
      initialValues={{
        checkInHand: false,
        confirmation: false,
        voidReason: '',
        voidReasonDescription: '',
      }}
      validationSchema={voidCheckSchema}
      details={<VoidCheckDetails checkDisbursement={checkDisbursement} />}
      form={
        <Flex display={'flex'} flexDirection={'column'} gap={1}>
          <VoidReason reasonList={checkVoidReasonOptions} />
          <VoidCheckInHand />
        </Flex>
      }
    />
  );
};
